package br.gov.go.pge.permanenciaweb.shared

import kotlinx.serialization.Serializable

/*
 * Wire models shared between the server plugin and the Kobweb frontend.
 * Everything here is a transport contract; domain types never leak out of the
 * process and wire shapes never leak into it.
 *
 * The inbound webhook payload (`EventPayload`) is server-only and lives in
 * `:server` (api/Dto.kt), together with the domain<->wire mapping functions.
 */

@Serializable
data class OccupantDto(
    val idpessoa: String,
    val nome: String,
    val tipoAcesso: String?,
    val telefone: String?,
    val cargo: String?,
    val setor: String?,
    val sala: String?,
    val andar: String?,
    val unidade: String?,
    val entrada: String,
    /** INCONSISTENTE | LIBERADO (see `AlertCode`); empty when the row has no alert. */
    val alertas: List<String> = emptyList(),
    /** HH:mm start of the window behind LIBERADO/EXPIRADO (null when there is no alert). */
    val liberadoDe: String? = null,
    /** HH:mm end of the same window. */
    val liberadoAte: String? = null,
    val dscDispositivo: String?,
    /** Non-permitted closes in the last 30 days; null/zero means no history. */
    val diasSemSaida30: Int? = null,
)

/** One missed-exit day, with the last known entry for that day. */
@Serializable
data class SemSaidaDayDto(
    val dia: String,
    val entradaEm: String,
)

/** One row of the "Sem saída (30d)" report. */
@Serializable
data class SemSaidaRowDto(
    val idpessoa: String,
    val nome: String,
    val tipoAcesso: String?,
    val cargo: String?,
    val setor: String?,
    val sala: String?,
    val andar: String?,
    val unidade: String?,
    val diasSemSaida: Int,
    val ultimoDia: String,
    /** Newest missed day first. */
    val dias: List<SemSaidaDayDto> = emptyList(),
)

@Serializable
data class RemovedDto(
    val removalId: Long,
    val idpessoa: String,
    val nome: String,
    val entrada: String,
    val removedAt: String,
    val removedBy: String,
    val note: String?,
)

@Serializable
data class ResolvedRemovalDto(
    val removalId: Long,
    val idpessoa: String,
    val nome: String,
    /** REENTERED | UNDONE | EXITED */
    val resolution: String,
    val removedAt: String,
    val removedBy: String,
    val note: String?,
    val resolvedAt: String,
)

@Serializable
data class CountsDto(
    val occupants: Int,
    val removed: Int,
    val semAndar: Int,
    val inconsistentes: Int,
    /** Occupants with an active stay permit right now. */
    val liberados: Int = 0,
)

@Serializable
data class SnapshotDto(
    val occupants: List<OccupantDto>,
    val removed: List<RemovedDto>,
    val resolvedRemovals: List<ResolvedRemovalDto>,
    val counts: CountsDto,
    val updatedAt: String,
    val lastEventAt: String?,
)

@Serializable
data class WsEnvelope(
    val type: String = "snapshot",
    val snapshot: SnapshotDto,
    /** Bumped on each ingest that actually inserted events; the events tab refetches on change. */
    val eventsVersion: Long = 0,
)

@Serializable
data class RemovalRequest(val idpessoa: String, val note: String? = null)

@Serializable
data class RemovalResponse(val id: Long)

@Serializable
data class OkResponse(val ok: Boolean = true)

@Serializable
data class WebhookResponse(val received: Int, val inserted: Int)

@Serializable
data class ErrorDto(val error: String)

@Serializable
data class EventDto(
    val idacessoLog: String,
    val horario: String,
    val sentido: String,
    val pessoa: String,
    val tipoAcesso: String?,
    val cargo: String?,
    val setor: String?,
    val andar: String?,
    val sala: String?,
    val unidade: String?,
    val fonteNome: String?,
    val dispositivo: String?,
)

// ------------------------------------------------------------------ acesso

/** Public (pre-login) settings used by the login page. */
@Serializable
data class PublicConfigDto(val contactEmail: String)

/** Signed-in user, read by the shell to gate the menu and the landing page. */
@Serializable
data class MeDto(
    val username: String,
    /** May open the roster/report (always an explicit grant). */
    val canViewReport: Boolean = false,
    /** May remove/undo manual roster removals. */
    val canManageRosterCorrections: Boolean = false,
    /** May open the "Liberações" form and create stay permits. */
    val canCreateStayPermits: Boolean = false,
    /** May list permits at all (own or all). */
    val canViewStayPermits: Boolean = false,
    /** May list every permit. */
    val canViewAllStayPermits: Boolean = false,
    /** May revoke permits at all (own or all). */
    val canRevokeStayPermits: Boolean = false,
    /** May revoke anybody's permits. */
    val canRevokeAllStayPermits: Boolean = false,
    /** May approve accounts and change permissions. */
    val canManageAccess: Boolean = false,
)

/** One account row of the admin users page. */
@Serializable
data class AdminUserDto(
    val username: String,
    val displayName: String?,
    val status: String,
    val requestedAt: String,
    val activatedAt: String?,
    val activatedBy: String?,
    val deniedAt: String?,
    val deniedBy: String?,
    val lastLoginAt: String?,
    /** Active grants only; revoked rows are fetched on demand for the history tab. */
    val grants: List<GrantDto> = emptyList(),
    /** Current `dim_chefia` membership (an org fact, not a permission). */
    val isLeadership: Boolean = false,
    val email: String? = null,
    /** LDAP | LEADERSHIP_SYNC — where the account came from. */
    val provisionedSource: String? = null,
) {
    val hasAccess: Boolean get() = grants.isNotEmpty()
}

@Serializable
data class AdminUsersDto(
    val pending: List<AdminUserDto>,
    val denied: List<AdminUserDto>,
    val active: List<AdminUserDto> = emptyList(),
)

/** Activation of a pending account or reactivation of a denied one. */
@Serializable
data class ActivateUserRequest(
    /** Preset to apply. Ignored when [grants] is supplied. */
    val preset: String = "USER",
    /** Explicit report grant (report is never part of a preset). */
    val report: Boolean = false,
    /** Desired editable grants; when present, replaces the preset/report pair. */
    val grants: List<GrantDto>? = null,
)

/** Batch replacement of the editable (non-sync) grants of an active account. */
@Serializable
data class UpdateAccessRequest(val grants: List<GrantDto>)

/** One row of the grant history (active and revoked). */
@Serializable
data class GrantHistoryDto(val grants: List<GrantDto>)

// -------------------------------------------------------------- diretório

/**
 * One row of the person directory pushed by the `dag_presenca_predio_directory` DAG.
 *
 * The directory is **reference metadata** (who this person is, where they work, when they were
 * last seen entering), not live state: a consumer that needs "is this person in the building right
 * now" must use the roster (`/api/roster`, `/ws`), which is fed by the event stream. The two may
 * legitimately diverge between snapshots.
 *
 * `cpfMascarado` is already masked at the producer (`***.456.789-**`): the full CPF never leaves
 * the sources, matching the events payload, which omits CPF entirely.
 */
@Serializable
data class DirectoryPersonDto(
    val idpessoa: String,
    val nome: String,
    val cpfMascarado: String? = null,
    /** `base_pessoa` type (SERVIDOR ADMINISTRATIVO, VISITANTE, ...). */
    val tipo: String? = null,
    val sala: String? = null,
    val andar: String? = null,
    val numrAndar: String? = null,
    val unidade: String? = null,
    val cargo: String? = null,
    /** Last ENTROU seen in the catracas at snapshot time (`dd/MM/yyyy HH:mm`); null when unknown. */
    val ultimoAcessoEm: String? = null,
    /** Reserved for the entrance-photo feature; null until that pipeline exists. */
    val fotoUrl: String? = null,
)

/** Full snapshot posted by the producer DAG; the app replaces its directory with it. */
@Serializable
data class DirectorySnapshotDto(
    val generatedAt: String,
    /** Optional producer-side content hash; an unchanged hash short-circuits the replace. */
    val hash: String? = null,
    val people: List<DirectoryPersonDto>,
)

@Serializable
data class DirectorySyncResponse(val received: Int, val replaced: Boolean)

/** One leadership account pushed by the `dim_chefia` task (idempotent, replace-all). */
@Serializable
data class LeadershipUserPayload(
    /** Optional override; when absent the app derives it from the e-mail local part. */
    val username: String? = null,
    val email: String? = null,
    val nome: String? = null,
    val unidade: String? = null,
)

/** 202 response of `POST /internal/leadership`: the apply runs in the background. */
@Serializable
data class LeadershipStartDto(
    val runId: Long,
    val received: Int,
    val resolved: Int,
    val created: Int,
    val updated: Int,
    val removed: Int,
    val ignored: Int,
    val baseline: Boolean = false,
    val warningCode: String? = null,
    val warning: String? = null,
)

/** One leadership sync run; polled by the DAG and shown in the admin page. */
@Serializable
data class LeadershipRunDto(
    val id: Long,
    val status: String,
    val received: Int = 0,
    val resolved: Int = 0,
    val created: Int = 0,
    val updated: Int = 0,
    val removed: Int = 0,
    val revokedGrants: Int = 0,
    val ignored: Int = 0,
    val baseline: Boolean = false,
    val warningCode: String? = null,
    val warning: String? = null,
    val error: String? = null,
    val startedAt: String? = null,
    val finishedAt: String? = null,
    val revertedAt: String? = null,
    /** Usernames removed by this run (for the review list and the revert preview). */
    val removedUsernames: List<String> = emptyList(),
)

// ------------------------------------------------------------ liberações

/** One stay permit row. Content is immutable: editing revokes and creates a new row. */
@Serializable
data class StayPermitDto(
    val id: Long,
    val batchId: Long,
    val idpessoa: String,
    val nome: String,
    val sala: String?,
    val andar: String?,
    val unidade: String?,
    val cargo: String?,
    /** ISO date (`yyyy-MM-dd`). */
    val dia: String,
    /** `HH:mm`. */
    val horaInicio: String,
    /** `HH:mm`; a value lower than `horaInicio` means the window ends on the next day. */
    val horaFim: String,
    val createdAt: String,
    val createdBy: String,
    val note: String?,
    val revokedAt: String?,
    val revokedBy: String?,
    /** True when the signed-in user may revoke this row (creator or admin). */
    val canRevoke: Boolean = false,
)

@Serializable
data class StayPermitListDto(val items: List<StayPermitDto>)

@Serializable
data class StayPermitRequest(
    val idpessoas: List<String>,
    /** ISO date (`yyyy-MM-dd`). */
    val inicio: String,
    /** ISO date (`yyyy-MM-dd`), at most 7 days after [inicio]. */
    val fim: String,
    /** `HH:mm`; defaults to 19:00. */
    val horaInicio: String? = null,
    /** `HH:mm`; defaults to 07:00 (next day). */
    val horaFim: String? = null,
    val note: String? = null,
)

@Serializable
data class StayPermitConflictDto(
    val idpessoa: String,
    val nome: String,
    val dia: String,
    /** Window of the row that already covers that (person, day). */
    val horaInicio: String,
    val horaFim: String,
)

@Serializable
data class StayPermitResultDto(
    /** Null when every requested (person, day) already had a covering permit. */
    val batchId: Long? = null,
    val created: Int,
    val conflicts: List<StayPermitConflictDto> = emptyList(),
)

@Serializable
data class RevokeStayPermitRequest(val reason: String? = null)
